import { useLoadingStore, selectLoading } from '@/modules/shared/stores/loadingStore'
import * as React from 'react'
import { useParams } from 'react-router-dom'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { ActivitiesEmptyState } from '@/modules/shared/components/ActivitiesEmptyState'
import { ActivityListHeader } from './ActivityListHeader'
import { getDriverActivitiesColumns } from '@/modules/drivers/constants/tables'
import ActivityListRow from './ActivityListRow'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useActivityContextMenu } from '@/modules/drivers/tabs/activities/context/ActivityContextMenuContext'
import { useActivity } from '@/modules/drivers/stores/activityStore'

import { createPortal } from 'react-dom'
import TachoDisc from '../../tacho_disc/TachoDisc'

const ActivityList = ({
  activities,
  onSelected,
  hoveredActivity,
  hoverFromChart,
  onActivityLeave,
  selectedActivities = [],
  onSelectionChange,
  onActivityAdd,
  onActivityEdit,
  canEdit = true,
  onShowChart,
}: {
  activities: any[]
  onSelected: (activity: any) => void
  hoveredActivity: any
  hoverFromChart: boolean
  onActivityLeave: () => void
  selectedActivities?: any[]
  onSelectionChange?: (activities: any[]) => void
  onActivityAdd?: (activity: any) => void
  onActivityEdit?: (activity: any, action: string, selected: any[]) => void
  canEdit?: boolean
  onShowChart?: (discId: number) => void
}) => {
  const loading = useLoadingStore(selectLoading)
  const { t } = useLocalize()
  const { openMenu } = useActivityContextMenu()
  const { triggerRefresh } = useActivity()
  const { companyId } = useParams<{ companyId: string }>()
  const columns = getDriverActivitiesColumns(t)
  const [chartDiscId, setChartDiscId] = React.useState<number | null>(null)
  const scrollContainerRef = React.useRef<HTMLDivElement>(null)
  const activityRefs = React.useRef<Map<string, HTMLTableRowElement>>(new Map())

  const sortedActivities = React.useMemo(() => {
    return [...activities].sort((a, b) => {
      const timeA = new Date(a.startTime).getTime()
      const timeB = new Date(b.startTime).getTime()
      return timeA - timeB
    })
  }, [activities])

  React.useEffect(() => {
    if (hoveredActivity && hoverFromChart && scrollContainerRef.current) {
      const activityElement = activityRefs.current.get(hoveredActivity.id)
      if (activityElement) {
        const container = scrollContainerRef.current
        const elementTop = activityElement.offsetTop

        const firstActivityId = sortedActivities[0]?.id
        if (hoveredActivity.id === firstActivityId) {
          container.scrollTo({
            top: 0,
            behavior: 'smooth',
          })
        } else {
          container.scrollTo({
            top: elementTop - 35,
            behavior: 'smooth',
          })
        }
      }
    }
  }, [hoveredActivity, hoverFromChart, sortedActivities])

  const toggleSelection = (activity: any) => {
    if (!onSelectionChange) return
    const isSelected = selectedActivities.some((a) => a.id === activity.id)
    if (isSelected) {
      onSelectionChange(selectedActivities.filter((a) => a.id !== activity.id))
    } else {
      onSelectionChange([...selectedActivities, activity])
    }
  }

  const handleRowContextMenu = (e: React.MouseEvent, activity: any) => {
    e.preventDefault()
    const targets =
      selectedActivities.length > 0 ? selectedActivities : [activity]
    const representative = targets[0]
    openMenu({
      x: e.clientX,
      y: e.clientY,
      activity: representative,
      canEdit,
      canGenerateAttestation: false,
      translate: t,
      onAdd: onActivityAdd,
      onEdit: (editedAct, action) =>
        onActivityEdit?.(editedAct, action, targets),
      onContextMenuChanged: () => {},
      addStartTime: null,
      hideAdd: true,
    })
  }

  return (
    <section className="tw-flex-1 tw-overflow-auto">
      <div className="tw-relative tw-h-full">
        {loading ? (
          <div className="tw-grid tw-h-full tw-place-items-center">
            <LoaderIcon className="tw-w-16 tw-animate-spin" />
          </div>
        ) : sortedActivities.length > 0 ? (
          <>
            <div
              className="tw-h-full tw-overflow-y-auto"
              ref={scrollContainerRef}
            >
              <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2 tw-pl-2">
                <ActivityListHeader columns={columns} />
                <tbody>
                  {sortedActivities.map((activity, index) => (
                    <ActivityListRow
                      key={activity.id || index}
                      ref={(el) => {
                        if (el) {
                          activityRefs.current.set(activity.id, el)
                        } else {
                          activityRefs.current.delete(activity.id)
                        }
                      }}
                      activity={activity}
                      onMouseEnter={() => onSelected(activity)}
                      onMouseLeave={onActivityLeave}
                      isHovered={hoveredActivity?.id === activity.id}
                      isSelected={selectedActivities.some(
                        (a) => a.id === activity.id,
                      )}
                      onClick={() => toggleSelection(activity)}
                      onContextMenu={(e) => handleRowContextMenu(e, activity)}
                      onShowChart={
                        // SourceType.TachographChart === 3 AND a linked chart id.
                        activity.source === 3 && activity.driverTachographChartId
                          ? () =>
                              (onShowChart ?? setChartDiscId)(
                                activity.driverTachographChartId,
                              )
                          : undefined
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="tw-grid tw-h-full tw-min-h-[min(100%,420px)] tw-w-full tw-place-items-center tw-px-4">
            <ActivitiesEmptyState
              title={t('dailyActivities.empty-activities-title')}
              subtitle={t('dailyActivities.empty-activities-subtitle')}
            />
          </div>
        )}
      </div>

   
        {!onShowChart &&
          chartDiscId != null &&
          createPortal(
            <TachoDisc
              companyId={companyId}
              discId={chartDiscId}
              onClose={() => setChartDiscId(null)}
            />,
            document.body,
          )}
    </section>
  )
}

export default ActivityList
